import React, { useEffect, useRef, useState } from 'react';
import { USE_WP_EMBED, WP_EMBED_SCRIPT_URL, WP_SITE_URL } from '../GlobalValues';

/**
 * Éléments personnalisés servis par le plugin WordPress « Theme Embed Widgets ».
 *
 * Le plugin expose `<wp-header>`, `<wp-footer>` et `<wp-widget slug="…">`, qu'il
 * hydrate depuis `/wp-json/theme-embed/v1/…` : le HTML du thème, son CSS préfixé
 * par `.wp-embed-scope`, et ses scripts. Tant qu'`embed.js` n'a pas hydraté,
 * ces balises ne rendent rien — le CSS critique de `public/index.html` les
 * masque pour éviter un flash de « Loading », et la réserve d'espace tient aux
 * variables `--header-h` / `--frise-h`.
 */

const LOADER_MARKER = 'data-wp-embed-loader';
const THEME_STYLESHEET_PREFIX = `${WP_SITE_URL}/wp-content/themes/`;

/**
 * Le plugin renvoie le CSS du thème deux fois, et une seule des deux copies est
 * utilisable.
 *
 * La bonne est dans le payload `styles` de l'API : préfixée `.wp-embed-scope`
 * côté PHP, elle n'habille que le chrome, et c'est elle qu'`embed.js` pose en
 * `<style data-wp-embed="scoped-…">`. La mauvaise est un `<link rel=stylesheet>`
 * vers le `style.css` **brut** du thème (~194 Ko, aucun préfixe), présent dans le
 * HTML du header *et* dans celui du footer. Comme ce HTML est posé par
 * `innerHTML`, le `<link>` se retrouve à l'intérieur du `<div class="wp-embed-scope">`
 * — sans que cela ne scope quoi que ce soit : une feuille de style s'applique au
 * document entier où qu'elle soit déclarée. Tout le thème repeint donc la page.
 *
 * Constaté sur les cartes d'avis : `.avis::before` du thème leur colle un cercle
 * jaune de 230px, et `.avis` un fond vert — alors que le storelocator les veut
 * blanches à ombre franche. La collision touche en réalité toutes les classes
 * que les deux feuilles partagent (`.btn`, `.container`, `.prix-card`, `section`…).
 *
 * On retire donc ces `<link>` de thème, et eux seuls : le CSS des extensions
 * (Gravity Forms pour la newsletter du footer) est laissé en place, et le chrome
 * garde son habillage puisque la copie scopée, elle, reste.
 *
 * Un `MutationObserver` armé avant l'injection du script plutôt qu'un nettoyage
 * sur l'évènement `load` : ce dernier n'est dispatché qu'après le chargement des
 * scripts du thème, ce qui laisserait le CSS brut peindre la page pendant
 * plusieurs centaines de millisecondes.
 *
 * ⚠️ Contournement d'un défaut du plugin, à signaler à l'équipe WordPress : le
 * jour où il cessera d'émettre ce `<link>`, ce code deviendra inutile (mais
 * restera inoffensif).
 */
const isRawThemeStylesheet = (node) =>
  node.tagName === 'LINK' && node.rel === 'stylesheet' && node.href.startsWith(THEME_STYLESHEET_PREFIX);

const dropRawThemeStylesheets = (root) => {
  if (!root.querySelectorAll) return;
  root.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
    if (isRawThemeStylesheet(link)) link.remove();
  });
};

/**
 * Injecte `embed.js` une fois par document, et seulement si le chrome WordPress
 * est actif, puis tient à l'écart le CSS brut du thème (voir ci-dessus).
 *
 * Le script vivait auparavant en dur dans `public/index.html`, ce qui le faisait
 * partir sur chaque page même avec `USE_WP_CHROME` à `false` — pour ~400 Ko de
 * CSS de thème dont personne ne voulait. L'injecter ici garde une seule source
 * de vérité pour l'URL du site WordPress (`WP_SITE_URL`).
 *
 * `embed.js` lit sa cible dans `document.currentScript.getAttribute('data-site')`
 * — renseigné, comme pour un script écrit dans le HTML. On ne met pas `defer` :
 * il est sans effet sur un script inséré par script, qui est déjà asynchrone.
 */
export const useWpEmbed = () => {
  useEffect(() => {
    if (!USE_WP_EMBED) return undefined;

    dropRawThemeStylesheets(document);

    const observer = new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          if (isRawThemeStylesheet(node)) node.remove();
          else dropRawThemeStylesheets(node);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    if (!document.querySelector(`script[${LOADER_MARKER}]`)) {
      const script = document.createElement('script');
      script.src = WP_EMBED_SCRIPT_URL;
      script.setAttribute('data-site', WP_SITE_URL);
      script.setAttribute(LOADER_MARKER, 'true');
      document.head.appendChild(script);
    }

    return () => observer.disconnect();
  }, []);
};

/**
 * `onLoad` est appelé avec l'élément une fois le header hydraté.
 *
 * C'est la seule accroche qui garantisse les deux conditions dont dépend
 * `Chrome.js` : le HTML du thème est dans le DOM (`innerHTML` est posé avant),
 * et `main.js` du thème a fini de s'exécuter (`loadScripts` est attendu avant
 * que le plugin ne dispatche `load`). L'écouteur est posé au montage, donc bien
 * avant que le `fetch` du plugin ne résolve.
 */
export const WpHeader = ({ onLoad }) => {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || !onLoad) return undefined;

    const handleLoad = () => onLoad(element);
    element.addEventListener('load', handleLoad);
    return () => element.removeEventListener('load', handleLoad);
  }, [onLoad]);

  return <wp-header ref={ref}></wp-header>;
};

export const WpFooter = () => <wp-footer></wp-footer>;

const WIDGET_TAG = 'wp-widget';

/**
 * Délai au-delà duquel on considère que le plugin ne viendra pas.
 *
 * Ne s'arme que si `<wp-widget>` n'est **pas encore un élément défini** : on
 * teste « embed.js a-t-il été chargé », pas « le réseau est-il lent ». Un widget
 * en cours de `fetch` sur une connexion poussive n'est donc jamais replié à
 * tort ; seul le cas où le script lui-même n'arrive pas déclenche ce garde-fou.
 */
const PLUGIN_LOAD_TIMEOUT_MS = 8000;

/**
 * Widget de contenu éditorial : `<wp-widget slug="bons-plans">`, avec repli sur
 * le rendu React statique.
 *
 * En cas d'échec, `embed.js` remplace le contenu du widget par un « Failed to
 * load widget » en anglais, au milieu d'une page publique. On préfère afficher
 * le composant `fallback` — celui qui servait déjà de spec au chantier WordPress
 * — encadré de rouge et surmonté d'un badge, pour que le contenu reste correct
 * tout en signalant sans ambiguïté que le widget n'est pas branché.
 *
 * Deux échecs sont couverts :
 *  - le widget répond mal (404 `widget_not_found`, erreur réseau, HTTP 5xx) :
 *    le plugin dispatche `error` sur l'élément ;
 *  - `embed.js` n'est jamais arrivé, donc `<wp-widget>` n'est jamais devenu un
 *    élément personnalisé et rien ne se produirait — d'où le garde-fou ci-dessus.
 *
 * Le repli remplace `<wp-widget>` dans l'arbre React : le message du plugin est
 * démonté avec lui, il n'y a donc jamais les deux à l'écran.
 */
export const WpWidget = ({ slug, fallback = null }) => {
  const ref = useRef(null);
  const [hasFailed, setHasFailed] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    let cancelled = false;
    const failOver = () => {
      if (!cancelled) setHasFailed(true);
    };

    element.addEventListener('error', failOver);

    let pluginTimer;
    if (!window.customElements || !window.customElements.get(WIDGET_TAG)) {
      pluginTimer = setTimeout(() => {
        if (!window.customElements || !window.customElements.get(WIDGET_TAG)) failOver();
      }, PLUGIN_LOAD_TIMEOUT_MS);
    }

    return () => {
      cancelled = true;
      clearTimeout(pluginTimer);
      element.removeEventListener('error', failOver);
    };
  }, [slug]);

  if (hasFailed) {
    return (
      <div className="wp-widget-fallback" data-slug={slug}>
        <p className="wp-widget-fallback-badge">Widget manquant, affichage statique</p>
        {fallback}
      </div>
    );
  }

  return <wp-widget ref={ref} slug={slug}></wp-widget>;
};

export default { useWpEmbed, WpHeader, WpFooter, WpWidget };
